/**
 * `recents`: the working set, for a fill (RFC 0284 S4c).
 *
 * WHAT THE SLICE WAS FOR. S4c said "move the working set onto the spaces
 * document", and gave two reasons: it should follow a person to another
 * machine, and a replacement sidebar should read it rather than reimplement it.
 * Only the second is a problem worth solving, and it does not need the move.
 *
 * `useOpenItems` argues the other way and is right: this is "a convenience over
 * a durable tree rather than a record of anything — the space's own tree is
 * what holds work, and this is only the tail of what fell out of it". A laptop
 * and a desk monitor have different tails, the same way §4f says they have
 * different widths. Syncing it would also put a document write behind every
 * pane you open.
 *
 * So the storage stays on the device and the ACCESS opens up. This is the
 * built-in sidebar's Home view, which is the one thing a replacement sidebar
 * could not draw.
 *
 * IDS, NOT ROWS, and deliberately. An id here may be a document or a session,
 * and a fill already has `docs` and `sessions` to resolve either.
 * Inventing a third row shape that unions them would give an author a type to
 * learn for no new information, and would go stale in a different way from the
 * two lists it was built from.
 */

export interface RecentsCapability {
  /**
   * What has been reached for in this space, most recent first, capped.
   *
   * Resolve each id against `docs.list()` or `sessions.list()`. An id in
   * neither has been deleted: drop the row rather than drawing a name for
   * something that no longer opens.
   */
  list(): string[]
  /** Fires when the working set changes. Returns an unsubscribe. */
  onChange(fn: () => void): () => void
}

export interface RecentsDeps {
  recents: () => readonly string[]
  subscribe: (fn: () => void) => () => void
}

export function makeRecents(deps: RecentsDeps): RecentsCapability {
  return {
    // COPIED, like `docs.list` and `sessions.list`: the array is the shell's
    // own, and a fill sorting it in place would reorder the built-in
    // sidebar's Home view from inside a module.
    list: () => [...deps.recents()],
    onChange: (fn) => deps.subscribe(fn),
  }
}
